import React from 'react';
import { Alert } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserTabTitle } from '../BrowserTabTitle';
import { useValidations } from '../../data/useValidations';
import { useAccount } from '../../auth';
import AQDataGrid from '../AQDataGrid';
import { useEntities, useEntity } from '../../data/flowEntities';
import ValidationsColDefs from './ValidationsColDefs';
import { useGlobalSettings } from '../../app/GlobalSettingProvider';
import { useQueries } from '@tanstack/react-query';
import { getPluralEntity } from '../../utils/entities';
import { Query } from '../../data/api';
import buildQuery from 'odata-query';
import { PageTitle } from '../PageTitle';

export const ValidationsList = () => {
  const { currentAccount } = useAccount();
  const { settings } = useGlobalSettings();
  const serviceQuery = useEntity(
    currentAccount.id,
    'api-service',
    'validations/v1',
    {
      include: ['api-entity'],
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
  );

  const serviceEndpointFound = !serviceQuery.isError && !serviceQuery.isLoading;

  const odataQuery = buildQuery({ top: 1000, skip: 0, count: true });
  const validationsQuery = useValidations(
    currentAccount.name,
    'validation',
    'v1',
    `validations${odataQuery}`,
    {
      // @ts-ignore
      enabled: serviceEndpointFound,
    },
  );

  // Extract unique destination_entity_id and destination_entity_type
  const getUniqueValidationsEntities =
    (srcORdest: 'source' | 'destination') => (validations: any) =>
      (validations || []).reduce((acc: any, validation: any) => {
        const key = `${validation[`${srcORdest}_entity_id`]}-${validation[`${srcORdest}_entity_type`]}`;

        if (!acc.has(key)) {
          acc.set(key, {
            entity_id: validation[`${srcORdest}_entity_id`],
            entity_type: validation[`${srcORdest}_entity_type`],
          });
        }
        return acc;
      }, new Map());

  // @ts-ignore
  const validationRecords = validationsQuery.data?.value || [];

  // @ts-ignore
  const uniqueValidationsEntitiesArray = Array.from(
    getUniqueValidationsEntities('destination')(validationRecords)?.values() ||
      [],
  ).concat(
    Array.from(
      getUniqueValidationsEntities('source')(validationRecords)?.values() || [],
    ),
  );

  const mappedQueries = uniqueValidationsEntitiesArray.map(
    // @ts-ignore
    ({ entity_id, entity_type }) => {
      const pluralEntity = getPluralEntity(entity_type)('entityHook');
      return {
        queryKey: [currentAccount.id, pluralEntity, entity_id],
        queryFn: () =>
          new Query(
            `accounts/${currentAccount.id}/${pluralEntity}/${entity_id}`,
          )
            .include(['integration-type'])
            .run(),
        // @ts-ignore
        refetchInterval: false,
        refetchOnWindowFocus: false,
      };
    },
  );

  // @ts-ignore
  const integrationsQueries = useQueries({ queries: mappedQueries });

  const integrationsMap = integrationsQueries.reduce(
    (acc: Record<string, any>, query) => {
      if (query.isSuccess) {
        const { data } = query;
        // @ts-ignore
        if (data && 'id' in data) {
          acc[(data as { id: string }).id] = data;
        }
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  const flows = useEntities(
    currentAccount.id,
    'flow',
    {
      include: ['source', 'destinations', 'next-run', 'last-run'],
    },
    {
      enabled: serviceEndpointFound,
    },
  );

  const transformRows =
    (flows = [], integrationsMap: any) =>
    (validationRecords: any[]) => {
      return validationRecords.map((record: any) => {
        const flow = (flows || [])?.find(
          (flow: any) => flow.id === record.flow_id,
        );

        return {
          ...record,
          flow_entity: flow,
          source: {
            type_name: integrationsMap[record.source_entity_id]?.type_name,
            img_url:
              integrationsMap[record.source_entity_id]?.['integration-type']?.[
                'img_url'
              ],
          },
          destination: {
            type_name: integrationsMap[record.destination_entity_id]?.type_name,
            img_url:
              integrationsMap[record.destination_entity_id]?.[
                'integration-type'
              ]?.['img_url'],
          },
        };
      });
    };

  return (
    <ErrorBoundary
      fallback={
        <Alert
          sx={{ maxWidth: '1200px', margin: '24px auto' }}
          severity="error"
        >
          Error fetching validations
        </Alert>
      }
    >
      <PageTitle />
      {serviceQuery.isError && (
        <Alert sx={{ maxWidth: '1200px', margin: '24px auto' }} severity="info">
          Validations need to be configured for this account
        </Alert>
      )}

      <section>
        <BrowserTabTitle>Data Validations</BrowserTabTitle>
      </section>
      {!serviceQuery.isError && (
        <AQDataGrid
          dataDependencies={[serviceQuery, validationsQuery, flows]}
          rowsTransformer={transformRows(
            flows?.data?.getData?.(),
            integrationsMap,
          )}
          colDefs={ValidationsColDefs(currentAccount, settings)}
          // @ts-ignore
          rows={validationRecords}
          sx={{
            height: 'auto',
            // adding padding to auto height cells
            '&.MuiDataGrid-columnHeaderTitleContainer': {
              textAlign: 'center',
            },

            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
              py: '12px',
            },
          }}
          gridProps={{
            getRowClassName: (params: any) =>
              `aq-datagrid-row--${params.row.severity}`,
            getEstimatedRowHeight: () => 200,
            getRowHeight: () => 'auto',
            sortingMode: 'client',
            filterMode: 'client',
            paginationMode: 'client',
          }}
        />
      )}
    </ErrorBoundary>
  );
};
